import { mapAndFormatDynamicValue, displayFnWrapper } from '../../utils/dynamic-data-retrievers';
import { currencyLov, PropertyType } from '../us/us.common';
import { map } from 'rxjs/operators';
import { LovValues, TemplateFieldsDisplayFn, DisplayFunctionOptions } from '../../../../models/template.model';
import { digger } from '@cbre/digger';
import { AVAILABILITY_TYPES, AvailabilityTypesLabels, USAGE_AMENITY_FIELD } from '../common';
import { usageAmenitiesData, getMediaInformation, amenitiesData } from '../../templates-formatter.const';
import { defaultTxt } from '@miq/utils';
import { ReportRecord } from '../../../../models/report.model';
import moment from 'moment';
import { Observable } from 'rxjs';
import { Country } from '../fields-args.const';

function setDefaultText(value: string[]) {
  return value.join() === defaultTxt ? ' ' : value;
}

export function PACIFIC_AVAILABILITY_INDUSTRIAL_COMMON_FIELDS(): TemplateFieldsDisplayFn {
  return {
    224: {
      displayFn: (record: ReportRecord, _options, arg) => {
        return mapAndFormatDynamicValue(record, arg)
          ? mapAndFormatDynamicValue(record, arg).toString().trim()
          : defaultTxt;
      },
    }, // Property Information
    348: {
      displayFn: (record: ReportRecord, _options, arg) => {
        return mapAndFormatDynamicValue(record, arg)
          ? mapAndFormatDynamicValue(record, arg).toString().trim()
          : defaultTxt;
      },
    }, // Listing Description
    242: {
      displayFn: (record: ReportRecord, _options, arg) => {
        return mapAndFormatDynamicValue(record, arg)
          ? mapAndFormatDynamicValue(record, arg).toString().trim()
          : defaultTxt;
      },
    }, // Location Information
    49: { displayFn: displayFnWrapper }, // Property Name
    233: {
      displayFn: (record: ReportRecord, options, askingPrice) => {
        return currencyLov(options).pipe(
          map((currencyLOV: LovValues[]) => {
            const uomArgument = { ...askingPrice.uomArgument, isPrefix: true };
            askingPrice = { ...askingPrice, uomArgument };
            return mapAndFormatDynamicValue(record, askingPrice, currencyLOV, true, true);
          })
        );
      },
      isVisible: (record: ReportRecord) => {
        const availableTypeVal = digger(record, 'data.ref_availability_type_desc');
        return availableTypeVal !== AVAILABILITY_TYPES.lease;
      },
    }, // Asking Sale Price
    235: { displayFn: displayFnWrapper }, // Rent (pa)
    25: {
      displayFn: displayFnWrapper,
      routeFn: ({ associatedProperty }) => {
        return ['/records', 'property', digger(associatedProperty, 'entity.id'), 'details'];
      },
    }, // Property Address
    223: { displayFn: displayFnWrapper }, // Contacts
    245: { displayFn: displayFnWrapper }, // Zoning
    248: {
      displayFn: (record: ReportRecord, _, columns) => {
        return columns.lovValues.pipe(
          map((lovs: LovValues[]) => {
            const resultKey = mapAndFormatDynamicValue(record, columns);
            const result = lovs.find(res => res.key === resultKey);
            return result ? result.value : defaultTxt;
          })
        );
      },
    }, // Columns
    249: { displayFn: displayFnWrapper }, // Roller Doors
    250: { displayFn: displayFnWrapper }, // Power
    50: { displayFn: displayFnWrapper }, // Location
    2: {
      displayFn: record => {
        const areaSum = digger(record, 'associatedProperty.property_measurement/property_measurement_size');
        const areaSumVal = !!areaSum ? areaSum.reduce((acc, val) => acc + val, 0) : 0;
        return areaSumVal;
      },
    }, // Total //TODO: need to replace with orginal id once added this label in MIQ DB
    259: {
      displayFn: (record: ReportRecord, _, availableType) => {
        const availableTypeVal = mapAndFormatDynamicValue(record, availableType) as string;
        const result = !!AvailabilityTypesLabels[availableTypeVal] ? AvailabilityTypesLabels[availableTypeVal] : '-';
        return result;
      },
    }, // title based on availability Type
    261: {
      displayFn: (record: ReportRecord, options, amenities) => {
        return amenitiesLov(options).pipe(
          map((amenitiesLOV: LovValues[]) => {
            const result = usageAmenitiesData(record, amenities, PropertyType.Industrial, 6, USAGE_AMENITY_FIELD);
            const lovData = amenitiesData(result, amenitiesLOV, options);
            return Array.isArray(lovData) ? setDefaultText(lovData) : lovData;
          })
        );
      },
    }, // Industrial Amenities
    263: {
      displayFn: (record: ReportRecord) => {
        return getMediaInformation(record);
      },
    }, // Floor Plan Images
    ...getFooterLabel(),
  };
}
export function getFooterLabel() {
  return {
    500: {
      displayFn: () => {
        return `Based on information available to CBRE as at ${moment().format(
          'DD MMMM YYYY'
        )}. The information in this document is general in nature and is a guide only. It does not take into account your individual circumstances. Before acting, you should check the accuracy of the information and seek your own independent financial advice and legal advice. The information must not be relied on to make any investment decisions. The principal and its agent will not be liable for your failure to verify the information or seek appropriate advice. © 2021 CBRE Inc.`;
      },
    },
    554: {
      displayFn: (record: ReportRecord, _, country) => {
        const countryName = mapAndFormatDynamicValue(record, country);
        return countryName === Country.NewZealand
          ? 'CBRE (Agency) Limited, Licensed Real Estate Agent (REAA 2008)'
          : '';
      },
      isVisible: (record: ReportRecord, settings) => {
        const country = settings.field.displayFnArguments[0];
        const countryValue = mapAndFormatDynamicValue(record, country);
        return countryValue === Country.NewZealand;
      },
    },
  };
}

export function amenitiesLov(options: DisplayFunctionOptions): Observable<LovValues[]> {
  const { locale, reportExportViewService } = options;
  const lov$ = reportExportViewService.getLovObservablesData(
    `/lookup/ref/country?lookupcategory=MIQ_PROPERTY_AMENITY`,
    undefined,
    locale
  ) as Observable<LovValues[]>;
  return lov$;
}
